import { Paper, Theme, Typography, useMediaQuery, useTheme } from '@mui/material';

import { GiftTypeEnum } from 'src/components/domain/gift-type-selection/GiftTypeSelections';
import { useLocale } from '@repo/i18n';
import SummaryGiftcardBooking from '../SummaryGiftcardBooking/SummaryGiftcardbooking';
import SummaryGiftcardPayment from '../SummaryGiftcardPayment/SummaryGiftcardPayment';
import SummaryGiftcardVat from '../SummaryGiftcardVat/SummaryGiftcardVat';
import { TZDate } from '@repo/tzdate';
import { GiftCardPromoCode } from 'src/components/domain/create-giftcard/GiftCardPromoCode';
import { useCallback, type JSX } from 'react';
import { useAtom } from 'ximple';
import { currencyAtom } from '@repo/widget-utils/currencyAtom';
import { capitalize } from '@repo/common-utils/TextUtils';
import { useConfigurations } from '@repo/widget-utils/widgetsConfiguration';
import { useCustomizations } from 'src/components/utils/theme/customizations';
import { buildGiftCardReceiptData } from 'src/utils/print/build-receipt-data';
import { printWidgetReceipt } from 'src/utils/print/print-receipt';

interface IProps {
    giftcardTotal: number;
    giftcardValue: number;
    selectedGiftType: GiftTypeEnum | null;
    showPrintButton?: boolean;
    showNextButton?: boolean;
    goPayClicked?: () => void;
    isCreatingGiftcard?: boolean;
    expiryDate?: TZDate;
    promoCodeWritable?: boolean;
    bookingRef?: string | null;
}

export default function SummaryGiftcardReceipt(props: IProps): JSX.Element {
    const {
        giftcardTotal,
        giftcardValue,
        showPrintButton = false,
        showNextButton = false,
        goPayClicked,
        isCreatingGiftcard = false,
        promoCodeWritable = false,
    } = props;
    const { t, locale } = useLocale();
    const theme = useTheme();
    const largeScreen = useMediaQuery((theme: Theme) => theme.breakpoints.up('md'));
    const [currency] = useAtom(currencyAtom);
    const configurations = useConfigurations();
    const customizations = useCustomizations();

    const onPrintReceipt = useCallback(() => {
        const logoUrl =
            (configurations.logo && configurations.logo.length > 0
                ? configurations.logo
                : customizations.iconUrl) || null;

        const giftCardLabelText =
            props.selectedGiftType === GiftTypeEnum.Print
                ? capitalize(t.purchased_giftcard.self)
                : capitalize(t.purchased_giftcard.recipient);

        const expiryDateText = props.expiryDate
            ? `${capitalize(t.valid_until)}: ${props.expiryDate.format('YYYY-MM-DD')}`
            : null;

        const data = buildGiftCardReceiptData({
            bookingRef: props.bookingRef ?? null,
            giftcardTotal,
            giftcardValue,
            giftCardLabelText,
            expiryDateText,
            logoUrl,
            locale,
            currency,
            t,
        });
        printWidgetReceipt(data);
    }, [
        giftcardTotal,
        giftcardValue,
        props.selectedGiftType,
        props.expiryDate,
        props.bookingRef,
        configurations.logo,
        customizations.iconUrl,
        locale,
        currency,
        t,
    ]);

    return (
        <Paper
            sx={{
                marginTop: theme.spacing(2),
                padding: theme.spacing(3, 3, 2, 3),
                [theme.breakpoints.down('md')]: {
                    maxWidth: '440px',
                },
                width: '100%',
                backgroundColor: theme.palette.grey[50],
                color: theme.palette.primary.main,
                '@media print': {
                    '& .no-print': {
                        display: 'none',
                    },
                },
            }}
            variant="outlined"
        >
            <Typography
                color="textSecondary"
                variant="h4"
                pb={theme.spacing(2)}
                fontWeight={500}
                fontSize={28}
            >
                {t.summary}
            </Typography>
            <SummaryGiftcardBooking
                giftcardTotal={giftcardValue}
                selectedGiftType={props.selectedGiftType}
                largeScreen={largeScreen}
                expiryDate={props.expiryDate}
            />
            <SummaryGiftcardVat giftcardPrice={giftcardValue} />
            <GiftCardPromoCode giftcardValue={giftcardValue} writable={promoCodeWritable} />
            <SummaryGiftcardPayment
                showPrintButton={showPrintButton}
                showNextButton={showNextButton}
                totalPrice={giftcardTotal}
                goPayClicked={goPayClicked}
                isCreatingGiftcard={isCreatingGiftcard}
                onPrint={onPrintReceipt}
            />
        </Paper>
    );
}
